// Hook for managing global budget configuration

import { useState, useEffect, useCallback } from 'react';
import {
  getBudgetConfiguration,
  saveBudgetConfiguration,
} from '@/lib/budget-configuration-storage';
import type {
  BudgetConfiguration,
  BudgetTrackingType,
} from '@/types/budget-configuration.types';

export function useBudgetConfiguration() {
  const [configuration, setConfiguration] = useState<BudgetConfiguration>(() =>
    getBudgetConfiguration()
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load configuration on mount
  useEffect(() => {
    const config = getBudgetConfiguration();
    setConfiguration(config);
  }, []);

  // Update tracking type
  const updateTrackingType = useCallback((trackingType: BudgetTrackingType) => {
    setConfiguration((prev) => ({
      ...prev,
      trackingType,
    }));
    setHasUnsavedChanges(true);
  }, []);

  // Save configuration
  const saveConfiguration = useCallback(async () => {
    setIsSaving(true);
    try {
      saveBudgetConfiguration(configuration);
      setHasUnsavedChanges(false);
      return { success: true };
    } catch (error) {
      console.error('Error saving configuration:', error);
      return { success: false, error };
    } finally {
      setIsSaving(false);
    }
  }, [configuration]);

  // Discard changes
  const discardChanges = useCallback(() => {
    const config = getBudgetConfiguration();
    setConfiguration(config);
    setHasUnsavedChanges(false);
  }, []);

  return {
    configuration,
    trackingType: configuration.trackingType,
    hasUnsavedChanges,
    isSaving,
    updateTrackingType,
    saveConfiguration,
    discardChanges,
  };
}
